// useIndexPage — samlar HELA handler/state-monteringen för /flow-weaver-
// sidan så att pages/Index.tsx bara är orkestrering + JSX (#3 i
// PRE_RELEASE_REVIEW_100). Hooken äger:
//
//   • dokument-CRUD (useDocuments) + co-op-wiring (useCoopWorkspace)
//   • autosave (dirty-tracking, mount-restore, silent mutation-logger)
//   • action-loggen
//   • alla feature-domain-handlers från /functions (block, group, save,
//     load, export, clear, keyboard, canvas, layer, wizard, inline)
//   • cross-sheet-linking, AI-staging, tour-staging
//   • keyboard-shortcuts, focus-trap, save-nudge, wizard-synk
//   • TopBar-handler-bag + mobile gesture-bag
//
// Den läser workflow/selection/dialog-state via contexts (useWorkflow,
// useSelectionMode, useDialogs) — samma mönster som övriga handler-hooks.

import { useState, useCallback, useRef, useEffect } from 'react';
import { useWorkflow } from '@/contexts/WorkflowContext';
import { useSelectionMode } from '@/contexts/SelectionModeContext';
import { useDialogs } from '@/contexts/DialogContext';
import { useAuth } from '@/contexts/AuthContext';
import { usePlan } from '@/hooks/usePlan';
import { useTranslation } from '@/i18n/LanguageContext';
import { useDocuments } from '@/hooks/useDocuments';
import { useCoopWorkspace } from '@/hooks/useCoopWorkspace';
import { useCrossSheetLinking } from '@/hooks/useCrossSheetLinking';
import { useKeyboardShortcuts } from '@/hooks/useKeyboardShortcuts';
import { useFocusTrap } from '@/hooks/useFocusTrap';
import { useAutoSave } from '@/hooks/useAutoSave';
import { useActionLog } from '@/hooks/useActionLog';
import { useSilentMutationLogger } from '@/hooks/useSilentMutationLogger';
import { useSaveNudge } from '@/hooks/useSaveNudge';
import { useOnboarding } from '@/components/onboarding/useOnboarding';
import { useAiStaging } from '@/hooks/useAiStaging';
import { useInlineHandlers } from '@/hooks/useInlineHandlers';
import { useTopBarHandlers } from '@/hooks/useTopBarHandlers';
import { WORKFLOW_PROFILE_KEY, type WorkflowProfile } from '@/lib/workflowTemplates';
import { WIZARD_DISMISSED_KEY } from '@/lib/wizardDismissal';
import type { BlockType } from '@/types/workflow';

import { useWizardHandler } from '@/functions/wizard';
import { useSaveHandlers } from '@/functions/save';
import { useLoadHandlers } from '@/functions/load';
import { useExportHandlers } from '@/functions/export';
import { useClearHandler } from '@/functions/clear';
import { useBlockHandlers } from '@/functions/block';
import { useGroupHandlers } from '@/functions/group';
import { useLayerHandlers } from '@/functions/layer';
import { useKeyboardHandlers } from '@/functions/keyboard';
import { useCanvasHandlers } from '@/functions/canvas';
import { useAutosaveRestore } from '@/functions/useAutosaveRestore';
import { useMobileGestures } from '@/functions/useMobileGestures';
import { useTourBlockAutoSelect } from '@/functions/useTourBlockAutoSelect';
import { useTourStaging } from '@/functions/useTourStaging';

export function useIndexPage({ viewOnly }: { viewOnly: boolean }) {
  const { t, tCount } = useTranslation();
  const { isAuthenticated, user } = useAuth();
  const { limits, tier } = usePlan();

  const {
    blocks, connectors, groups,
    selectedBlockId, workflowName,
    setWorkflowName, setSelectedBlockId,
    createBlock, updateBlock, deleteBlock,
    convertBlockToLayer,
    createConnector, deleteConnector, createGroup, updateGroup, updateGroups,
    clearCanvas, loadState, appendState, getState, getActiveSheetState, updateActiveSheet,
    undo, redo, canUndo,
    activeSheetIndex,
    createCrossSheetConnector,
    currentLayerGroupId,
    navigateInto, navigateToLevel, resetLayerPath,
  } = useWorkflow();

  const {
    isConnected, connectionDebugInfo,
    saveDocument, listDocuments, loadDocument, loadCoopWorkflow, setCoopEnabled, deleteDocument,
  } = useDocuments();

  const { isDirty, markDirty, markClean } = useAutoSave(getState);
  const { entries: actionLogEntries, logAction, clearLog: clearActionLog, snapshot: snapshotActionLog } = useActionLog();

  // Silent logger: observerar canvas-mutations och registrerar redacted
  // action-log-entries (bara ids + kind + skalärer, ingen PII).
  useSilentMutationLogger({ blocks, connectors, groups, logAction, enabled: !viewOnly });

  // Mount-only restore av senaste autosave-snapshot (compact + legacy).
  useAutosaveRestore({ viewOnly, loadState, logAction });

  useEffect(() => { markDirty(); }, [blocks, connectors, groups, workflowName, markDirty]);

  const canvasInnerRef = useRef<HTMLDivElement>(null);

  const {
    selectedGroupId, setSelectedGroupId,
    selectedConnectorId, setSelectedConnectorId,
    setConnectMode,
    groupMode, setGroupMode,
    groupSelection, setGroupSelection,
  } = useSelectionMode();

  const [currentDocumentId, setCurrentDocumentId] = useState<string | null>(null);

  // Co-op wiring lives in useCoopWorkspace: URL parse, RLS load, realtime.
  const { realtime, isCoopLoading } = useCoopWorkspace({
    user,
    isAuthenticated,
    currentDocumentId,
    blocks,
    connectors,
    groups,
    getActiveSheetState,
    loadState,
    resetLayerPath,
    setCurrentDocumentId,
    loadCoopWorkflow,
    t,
    tier,
  });

  const dlg = useDialogs();
  // B5 (launch plan): nudge unauthenticated users to create an account
  // after 5 min of unsaved work. Opens the auth dialog (sign-up flow).
  useSaveNudge({
    isDirty,
    onSignUp: () => dlg.setLoginOpen(true),
  });
  const { setWizardOpen: setOnboardingWizardOpen } = useOnboarding();
  // #32: när wizarden stängs (oavsett complete eller X) skrivs en
  // dismissed-flagga så auto-open inte naggar samma device igen.
  // Index.tsx läser flaggan när initialWizardOpen beräknas.
  const prevWizardOpenRef = useRef(dlg.wizardOpen);
  useEffect(() => {
    setOnboardingWizardOpen(dlg.wizardOpen);
    if (prevWizardOpenRef.current && !dlg.wizardOpen) {
      try { localStorage.setItem(WIZARD_DISMISSED_KEY, '1'); } catch { /* quota/private mode */ }
    }
    prevWizardOpenRef.current = dlg.wizardOpen;
  }, [dlg.wizardOpen, setOnboardingWizardOpen]);

  const [workflowProfile, setWorkflowProfile] = useState<WorkflowProfile | null>(() => {
    try {
      const raw = localStorage.getItem(WORKFLOW_PROFILE_KEY);
      return raw ? JSON.parse(raw) : null;
    } catch { return null; }
  });

  // Canvas announcements + click
  const { announcement, announce, handleCanvasClick } = useCanvasHandlers({ setSelectedBlockId, setSelectedGroupId });

  // Feature-domain handlers (all extracted to /functions)
  const { handleNavigateToLayer } = useLayerHandlers({ groups, navigateInto, navigateToLevel, resetLayerPath });

  const {
    handleSelectBlock, handleDeleteSelected,
    handleUpdateBlockIcon, handleUpdateBlockColor, handleConvertToLayer,
  } = useBlockHandlers({
    blocks, selectedBlockId, setSelectedBlockId, setSelectedGroupId,
    setSelectedConnectorId,
    deleteBlock, updateBlock, convertBlockToLayer, logAction, announce, t,
  });

  const {
    handleToggleConnectMode, handleToggleGroupMode,
    handleToggleGroupSelection, handleToggleGroupCollapse,
    handleUpdateGroupPosition, handleUpdateGroupColor, handleToggleGroupLock,
    handleSelectGroup,
    handleCreateGroupFromIds, handleAddBlockToGroup,
    handleCollapseAll, handleCollapseToLayers, handleExpandAll,
  } = useGroupHandlers({
    blocks, groups, groupMode, groupSelection, currentLayerGroupId,
    setSelectedBlockId, setSelectedGroupId, setSelectedConnectorId,
    setGroupMode, setGroupSelection, setConnectMode,
    createGroup, updateGroup, updateGroups, logAction, t, tCount,
  });

  const { handleClearRequest, handleClearConfirmed } = useClearHandler({
    blockCount: blocks.length, connectorCount: connectors.length,
    clearCanvas, setConnectMode, setGroupMode, setGroupSelection,
    setCurrentDocumentId, resetLayerPath, setConfirmClearOpen: dlg.setConfirmClearOpen,
    logAction, announce, t,
  });

  const { handleUndo, handleRedo, handleEscape } = useKeyboardHandlers({
    undo, redo,
    setSelectedBlockId, setSelectedGroupId,
    setConnectMode, setGroupMode, setGroupSelection,
    logAction, announce,
  });

  const { handleSave, handleSaveAs, handleSaveToDb, handleQuickUpdateToCloud } = useSaveHandlers({
    getState,
    workflowName, setWorkflowName,
    currentDocumentId, setCurrentDocumentId,
    markClean, logAction, t, saveDocument,
    setSaveAsNew: dlg.setSaveAsNew, setSaveDialogOpen: dlg.setSaveOpen,
  });

  const { handleLoad, handleLoadFromDb, handleDeleteFromDb, checkAndRouteIfOverLimit } = useLoadHandlers({
    loadState, resetLayerPath,
    setCurrentDocumentId, currentDocumentId,
    loadDocument,
    deleteDocument,
    limits,
    logAction, t, tCount,
  });

  const { handleExportPNG, handleExportView, handleExportAll } = useExportHandlers({
    canvasInnerRef, workflowName, blocks, connectors, groups,
    setExportPngDialogOpen: dlg.setExportPngOpen, t,
  });

  const handleWizardComplete = useWizardHandler({
    setWizardOpen: dlg.setWizardOpen, setWorkflowProfile, logAction,
  });

  // Inline glue-handlers: connect-toggle, save-to-cloud, new-workflow
  // chooser, simulate, JSON import/copy, focus-property, duplicate,
  // explain, delete. Plus explain-block-state för ExplainBlockPanel.
  const {
    handleConnect, handleSaveToCloud, handleNewWorkflowCreate,
    handleSimulate, handleImportJson,
    handleFocusBlockProperty, handleDuplicateBlock,
    handleExplainBlock, handleDeleteBlockById,
    explainBlockId, setExplainBlockId,
  } = useInlineHandlers({
    blocks, createBlock, updateBlock, deleteBlock,
    createConnector,
    setSelectedBlockId, setSelectedGroupId,
    workflowName, clearCanvas, setCurrentDocumentId, resetLayerPath,
    loadState, appendState, planLimits: limits,
    getState,
    openSaveDialogForUpdate: () => { dlg.setSaveAsNew(false); dlg.setSaveOpen(true); },
    quickUpdateToCloud: handleQuickUpdateToCloud,
    logAction, announce,
    t, tCount, connectorsLength: connectors.length,
  });

  // Cross-sheet linking — mutex with connect/group modes; source on sheet A,
  // target on adjacent sheet B. Hook owns the source ref + Esc handling.
  const {
    crossSheetMode, crossSheetSource,
    handleToggleCrossSheetMode, handleCrossSheetPick, startCrossSheetFromBlock,
  } = useCrossSheetLinking({
    blocks, activeSheetIndex, createCrossSheetConnector,
    setConnectMode, setGroupMode, setGroupSelection,
    logAction, announce,
  });

  // Route block selection through cross-sheet pick when the mode is on.
  const handleSelectBlockRouted = useCallback((id: string) => {
    if (crossSheetMode) {
      handleCrossSheetPick(id);
      return;
    }
    handleSelectBlock(id);
  }, [crossSheetMode, handleCrossSheetPick, handleSelectBlock]);

  useKeyboardShortcuts({
    onSave: handleSave,
    onUndo: handleUndo,
    onRedo: handleRedo,
    onDelete: () => {
      // Connector selection takes priority over block selection (mutex
      // is enforced in SelectionModeContext, but be defensive here too).
      if (selectedConnectorId) {
        const connectorExists = connectors.some(c => c.id === selectedConnectorId);
        if (connectorExists) {
          deleteConnector(selectedConnectorId);
          setSelectedConnectorId(null);
          announce('Connector deleted');
          return;
        }
      }
      handleDeleteSelected();
    },
    onEscape: handleEscape,
    onShowHelp: () => dlg.setShortcutsOpen(true),
  });
  useFocusTrap();

  // Tour-steg som pekar på properties-fält auto-selectar första blocket
  // om inget är valt (lyssnar på `ensure:block-selected`).
  useTourBlockAutoSelect({ viewOnly, setSelectedGroupId });

  // AI credit-gated staging: preflight, stage, apply, discard,
  // regression-check, artifact-version-persist, mount-cleanup.
  const {
    aiPreview,
    aiPreviewPending,
    regressionDialog,
    setRegressionDialog,
    lockedWhilePreview,
    handleStageAiProposal,
    handleApplyAiProposal,
    handleDiscardAiProposal,
    handleRegressionApplyAnyway,
    handleRegressionCancel,
    handleRegressionRetry,
  } = useAiStaging({
    viewOnly,
    getActiveSheetState,
    loadState,
    updateActiveSheet,
    logAction,
    openAiAssistant: () => dlg.setAiAssistantOpen(true),
  });

  // Tour-staging: guided tours kör i temporär sandbox-layer som rensas
  // vid tour:end. Cleanup av stale sandboxes (post-crash) körs vid mount.
  useTourStaging({ viewOnly, getActiveSheetState, loadState, logAction });

  // TopBar-handler-bag: alla lockedWhilePreview-wrappade callbacks
  // samlade så TopBar-JSX bara behöver {...topBarHandlers}.
  const topBarHandlers = useTopBarHandlers({
    lockedWhilePreview,
    handleSave, handleSaveAs, handleSaveToCloud,
    handleLoad, handleExportPNG, handleSimulate, handleClearRequest,
    // ExportJsonDialog hanterar själva clipboard/download — onCopyJson
    // öppnar bara dialogen så user kan toggla "Export all".
    handleCopyJson: dlg.openExportJson,
    openNewWorkflow: () => dlg.setNewWorkflowOpen(true),
    openImport: () => dlg.setImportOpen(true),
    openHelp: () => dlg.setHelpOpen(true),
    openAiJson: () => dlg.setAiJsonOpen(true),
    openAiAssistant: () => dlg.setAiAssistantOpen(true),
    openOverview: () => dlg.setOverviewOpen(true),
    openCloudWorkspace: () => dlg.setCloudWorkspaceOpen(true),
    openShareWorkspace: () => dlg.setShareWorkspaceOpen(true),
    openLogin: () => dlg.setLoginOpen(true),
    openRepoUpload: dlg.openRepoUpload,
  });

  // Mobile gesture-handlers — bag som passas till WorkflowCanvas via
  // shell:erna. Se useMobileGestures för long-press/CustomEvent-detaljer.
  const mobileGestures = useMobileGestures({ canUndo, onUndo: handleUndo });

  const selectedGroup = groups.find(g => g.id === selectedGroupId) || null;

  const onCreateBlock = (type: string, x: number, y: number, nameOverride?: string) => {
    const block = createBlock(type as BlockType, x, y);
    if (nameOverride) {
      // Apply the preset label carried in dataTransfer so the block inherits
      // the use-case name ("Person"/"Server"/etc) instead of the generic
      // default ("Aktivitet"). Done as a follow-up update so we don't have
      // to thread nameOverride through every createBlock callsite.
      updateBlock(block.id, { config: { ...block.config, name: nameOverride } });
    }
    if (currentLayerGroupId) {
      updateGroup(currentLayerGroupId, {
        blockIds: [...(groups.find(g => g.id === currentLayerGroupId)?.blockIds || []), block.id],
      });
    }
    const displayName = nameOverride || block.config.name;
    logAction('Block created', displayName);
    announce(`Block created: ${displayName}`);
    requestAnimationFrame(() => requestAnimationFrame(() => {
      const el = document.querySelector(`[data-block-id="${block.id}"]`) as HTMLElement;
      el?.focus();
    }));
  };

  return {
    // Connection / co-op / dokument
    isConnected, connectionDebugInfo, realtime, isCoopLoading,
    currentDocumentId, setCurrentDocumentId,
    listDocuments, setCoopEnabled,
    // Autosave / action-log
    isDirty, actionLogEntries, clearActionLog, snapshotActionLog,
    // Onboarding-profil
    workflowProfile,
    // Canvas
    canvasInnerRef, announcement, handleCanvasClick, onCreateBlock,
    selectedGroup,
    // Block / group / layer
    handleSelectBlockRouted, handleSelectGroup, handleNavigateToLayer,
    handleUpdateBlockIcon, handleUpdateBlockColor, handleConvertToLayer,
    handleToggleConnectMode, handleToggleGroupMode, handleToggleGroupSelection,
    handleToggleGroupCollapse, handleUpdateGroupPosition, handleUpdateGroupColor,
    handleToggleGroupLock, handleCreateGroupFromIds, handleAddBlockToGroup,
    handleCollapseAll, handleCollapseToLayers, handleExpandAll,
    // Save / load / export / clear / undo
    handleSave, handleLoad, handleExportPNG, handleSimulate,
    handleClearRequest, handleClearConfirmed,
    handleSaveToDb, handleLoadFromDb, handleDeleteFromDb, checkAndRouteIfOverLimit,
    handleExportView, handleExportAll,
    handleUndo, handleRedo,
    // Wizard / inline glue
    handleWizardComplete, handleNewWorkflowCreate, handleConnect,
    handleImportJson, handleFocusBlockProperty, handleDuplicateBlock,
    handleExplainBlock, handleDeleteBlockById,
    explainBlockId, setExplainBlockId,
    // Cross-sheet
    crossSheetMode, crossSheetSource, handleToggleCrossSheetMode,
    startCrossSheetFromBlock,
    // AI-staging
    aiPreview, aiPreviewPending, regressionDialog, setRegressionDialog,
    handleStageAiProposal, handleApplyAiProposal, handleDiscardAiProposal,
    handleRegressionApplyAnyway, handleRegressionCancel, handleRegressionRetry,
    // Bags
    topBarHandlers, mobileGestures,
  };
}
